/**
 * Photo URL handling for S3-backed images.
 *
 * The backend mints **presigned S3 URLs with a 15-minute lifetime**
 * (condo-manager services/s3_service.go: PresignedURLExpiration = 15 * time.Minute,
 * and the per-call 15*time.Minute arguments in person_handler.go). Those URLs are
 * embedded in API responses and then held in React state for as long as the screen
 * stays mounted.
 *
 * On the operational screen that is a problem: the view is a long-lived kiosk-style
 * surface. After ~15 minutes of sitting idle, every presigned URL in state is dead,
 * so any re-render or newly-arriving event that references one paints a broken image.
 * Nothing is wrong with the session — the *link* expired.
 *
 * The fix is to stop using expiring links for display at all. condo-manager already
 * serves a stable, non-expiring image proxy:
 *
 *     GET /api/v1/images/{bucket}/{key}     (no auth required for display,
 *                                            Cache-Control: public, max-age=3600)
 *
 * A presigned URL already contains the bucket and the key, so rewriting it to the
 * proxy path is a pure string transformation. That matters for the flooding concern:
 * recovering from an expired photo link costs **zero extra API requests**, because we
 * never have to go ask for a fresh signature. There is no retry storm to rate-limit,
 * no refetch to debounce, and no cache to invalidate — the rewritten URL simply does
 * not expire.
 *
 * Reacting to a load error would be the obvious alternative, and it is the one that
 * floods: a grid of 60 faces all expire in the same second, so all 60 would fail and
 * all 60 would ask for a new URL at once. Rewriting up front avoids that class of
 * problem instead of managing it.
 */

/** A bucket/key pair extracted from an S3 URL. */
export interface S3Location {
  bucket: string;
  key: string;
}

/** Query parameters AWS SigV4 adds when presigning. */
const SIGV4_SIGNATURE_PARAM = 'X-Amz-Signature';
const SIGV4_DATE_PARAM = 'X-Amz-Date';
const SIGV4_EXPIRES_PARAM = 'X-Amz-Expires';

/** Path prefix of condo-manager's stable image proxy. */
const IMAGE_PROXY_PREFIX = '/api/v1/images';

/**
 * Parse a URL without throwing. Relative URLs are resolved against a dummy origin
 * so callers can hand us either shape.
 */
const safeParse = (url: string): URL | null => {
  try {
    return new URL(url, 'http://placeholder.invalid');
  } catch {
    return null;
  }
};

/**
 * True when the URL carries a SigV4 signature, meaning it has a finite lifetime and
 * will eventually start returning 403.
 */
export const isPresignedUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const parsed = safeParse(url);
  return parsed?.searchParams.has(SIGV4_SIGNATURE_PARAM) ?? false;
};

/** True when the URL already points at the stable image proxy. */
export const isProxyPhotoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const parsed = safeParse(url);
  return parsed?.pathname.startsWith(`${IMAGE_PROXY_PREFIX}/`) ?? false;
};

/**
 * Extract bucket and key from an S3 URL, in either addressing style.
 *
 * Virtual-hosted (the AWS default, and what production uses since the client is built
 * with no UsePathStyle override):
 *
 *     https://my-bucket.s3.us-east-1.amazonaws.com/people/abc.jpg?X-Amz-...
 *
 * Path-style (LocalStack and other custom endpoints, where UsePathStyle is set):
 *
 *     http://localstack:4566/my-bucket/people/abc.jpg?X-Amz-...
 *
 * Returns null for anything that is not recognisably an S3 object URL. The query
 * string is always dropped — it holds the signature, not part of the key.
 */
export const parseS3Url = (url: string | null | undefined): S3Location | null => {
  if (!url) return null;

  const parsed = safeParse(url);
  if (!parsed) return null;

  // Keys may contain characters that were percent-encoded in the URL.
  const decodePath = (p: string): string => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;
    }
  };

  const path = parsed.pathname.replace(/^\/+/, '');
  const host = parsed.hostname;

  // Virtual-hosted: {bucket}.s3.amazonaws.com or {bucket}.s3.{region}.amazonaws.com
  const virtualHosted = /^(.+?)\.s3[.-](?:[a-z0-9-]+\.)?amazonaws\.com$/i.exec(host);
  if (virtualHosted) {
    const bucket = virtualHosted[1];
    if (!bucket || !path) return null;
    return { bucket, key: decodePath(path) };
  }

  // Path-style: the first path segment is the bucket. Only treated as S3 when the
  // host looks like S3 or the URL is presigned, so an unrelated CDN link that
  // happens to have two path segments is not misread as a bucket reference.
  const looksLikeS3Host = /(^|\.)s3[.-]/i.test(host) || /amazonaws\.com$/i.test(host);
  if (looksLikeS3Host || isPresignedUrl(url)) {
    const slash = path.indexOf('/');
    if (slash <= 0) return null;
    const bucket = path.slice(0, slash);
    const key = path.slice(slash + 1);
    if (!bucket || !key) return null;
    return { bucket, key: decodePath(key) };
  }

  return null;
};

/**
 * Build the stable proxy path for an S3 location. Each key segment is encoded
 * individually so that slashes in the key stay as path separators — the backend route
 * is /images/:bucket/*key and reassembles the remainder verbatim.
 */
export const buildProxyPhotoUrl = ({ bucket, key }: S3Location): string => {
  const encodedKey = key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${IMAGE_PROXY_PREFIX}/${encodeURIComponent(bucket)}/${encodedKey}`;
};

/**
 * Rewrite an expiring presigned S3 URL to the stable image-proxy path.
 *
 * Returns null when the URL is not a recognisable S3 object URL, so callers can fall
 * back to using it unchanged. Non-presigned S3 URLs are rewritten too: they are
 * equally well served by the proxy, and routing them through it keeps every photo on
 * one code path.
 */
export const toProxyPhotoUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (isProxyPhotoUrl(url)) return url;

  const location = parseS3Url(url);
  if (!location) return null;

  return buildProxyPhotoUrl(location);
};

/**
 * Seconds until a presigned URL expires. Negative once expired, null when the URL is
 * not presigned or lacks the parameters needed to tell.
 *
 * X-Amz-Date is ISO8601 basic format (20260928T120000Z) and X-Amz-Expires is a
 * lifetime in seconds from that instant.
 */
export const presignedUrlSecondsRemaining = (
  url: string | null | undefined,
  now: number = Date.now()
): number | null => {
  if (!url) return null;

  const parsed = safeParse(url);
  if (!parsed) return null;

  const amzDate = parsed.searchParams.get(SIGV4_DATE_PARAM);
  const amzExpires = parsed.searchParams.get(SIGV4_EXPIRES_PARAM);
  if (!amzDate || !amzExpires) return null;

  const lifetime = Number(amzExpires);
  if (!Number.isFinite(lifetime)) return null;

  // 20260928T120000Z → 2026-09-28T12:00:00Z
  const iso = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate);
  if (!iso) return null;
  const [, y, mo, d, h, mi, s] = iso;
  const signedAt = Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
  if (Number.isNaN(signedAt)) return null;

  return Math.round((signedAt + lifetime * 1000 - now) / 1000);
};

/**
 * True when a presigned URL is expired, or close enough that a load started now would
 * likely lose the race. Unknown/non-presigned URLs are reported as not expired —
 * there is nothing to act on.
 */
export const isPresignedUrlExpired = (
  url: string | null | undefined,
  skewSeconds: number = 30,
  now: number = Date.now()
): boolean => {
  const remaining = presignedUrlSecondsRemaining(url, now);
  if (remaining === null) return false;
  return remaining <= skewSeconds;
};

/**
 * Resolve any stored or API-supplied photo URL to the best URL to display.
 *
 * Order of preference:
 *   1. the stable proxy path, whenever the URL is a recognisable S3 object URL
 *   2. the URL unchanged, for anything else (already-proxied paths, relative paths,
 *      data: URIs, blob: URLs, third-party hosts)
 *
 * `absolutize` is injected so this module stays free of environment configuration;
 * api.ts supplies the base-URL logic.
 */
export const resolveDisplayPhotoUrl = (
  url: string | null | undefined,
  absolutize: (relative: string) => string
): string | null => {
  if (!url) return null;

  // Nothing to rewrite for inline or object URLs.
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  const proxyPath = toProxyPhotoUrl(url);
  if (proxyPath) {
    return isProxyPhotoUrl(url) && /^https?:\/\//i.test(url)
      ? url // already an absolute proxy URL
      : absolutize(proxyPath);
  }

  return /^https?:\/\//i.test(url) ? url : absolutize(url);
};
